import { Background } from '@/components';
import { IBlueprint } from '@/supabase';
import { getImageWidthFromFile, getImageWidthFromUrl } from '@/utils';
import { useMeasure } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

type ImagePreviewProps = {
  file?: File;
  url?: string;
  placeholder?: string;
  background?: IBlueprint['background'];
};

export const ImagePreview = ({
  file,
  url,
  placeholder,
  background,
}: ImagePreviewProps) => {
  const [imageFileWidth, setImageFileWidth] = useState<number>();
  const [objectUrl, setObjectUrl] = useState<string>();
  const [previewRef, { width: previewWidth }] = useMeasure();

  useEffect(() => {
    if (file)
      getImageWidthFromFile(file).then(width => setImageFileWidth(width));
    else if (url)
      getImageWidthFromUrl(url).then(width => setImageFileWidth(width));
  }, [file, url]);

  useEffect(() => {
    if (!file) {
      setObjectUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(file);
    setObjectUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  if ((!url && !file) || (url && !url.startsWith('https://')))
    return (
      <div className="flex min-h-16 w-full items-center justify-center whitespace-pre-line rounded-lg bg-steel-800 px-3 py-2 text-center text-steel-300">
        {placeholder || 'Preview will show here'}
      </div>
    );

  if (file) {
    return (
      <div className="relative w-full rounded-lg object-contain">
        {background && imageFileWidth && previewWidth && (
          <Background
            background={background}
            imageWidth={imageFileWidth}
            previewWidth={previewWidth}
          />
        )}
        {objectUrl && (
          <img
            ref={previewRef}
            src={objectUrl}
            alt="preview"
            className="relative"
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex w-full items-center justify-center rounded-lg object-contain">
      {background && imageFileWidth && previewWidth && (
        <Background
          background={background}
          imageWidth={imageFileWidth}
          previewWidth={previewWidth}
        />
      )}
      <img ref={previewRef} src={url} alt="preview" className="relative" />
    </div>
  );
};
